import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, FileText, Globe } from 'lucide-react';
import { WhatsAppIcon } from './WhatsAppIcon';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "production" ? "https://stigmafloraweb.onrender.com/api" : "http://localhost:8000/api");

const LOGO_URL = "/file.svg";
const BROCHURE_URL = "/brochures/stigmaflora-brochure.pdf";
const WHATSAPP_NUMBER = "917702099393";

// Function to open WhatsApp
const openWhatsApp = () => {
  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hello Stigmaflora! I would like to know more about your products.")}`, '_blank', 'noopener,noreferrer');
};

// Function to open brochure
const openBrochure = () => {
  window.open(BROCHURE_URL, '_blank', 'noopener,noreferrer');
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'te' : 'en';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.products'), path: '/products' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-1' : 'bg-transparent py-2'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center group"
          >
            <img 
              src={LOGO_URL} 
              alt="Stigmaflora Agri Science" 
              className="h-10 w-auto group-hover:scale-105 transition-transform"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-sm font-medium px-4 py-2 rounded-full transition-colors ${
                  isActive(link.path)
                    ? 'bg-emerald-100 text-emerald-900'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-full transition-colors cursor-pointer mr-2 border border-emerald-200"
                title="Toggle Language"
              >
                <Globe className="w-4 h-4" />
                {i18n.language === 'en' ? 'తెలుగు' : 'English'}
              </button>

              <Button
                data-testid="nav-brochure-btn"
                variant="outline"
                size="sm"
                onClick={openBrochure}
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-full h-9 px-4 cursor-pointer"
              >
                <FileText className="w-4 h-4 mr-1.5" />
                {t('nav.brochure')}
              </Button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-emerald-900"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden absolute top-14 left-0 right-0 bg-white shadow-lg border-t border-slate-100"
          >
            <div className="flex flex-col p-3 gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  onClick={() => setIsOpen(false)}
                  className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-emerald-50 text-emerald-900'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-2">
                <button
                  onClick={() => { setIsOpen(false); toggleLanguage(); }}
                  className="flex items-center justify-center gap-2 w-full py-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-full transition-colors cursor-pointer border border-emerald-200"
                >
                  <Globe className="w-4 h-4" />
                  {i18n.language === 'en' ? 'Switch to తెలుగు' : 'Switch to English'}
                </button>
                <div className="mt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setIsOpen(false); openBrochure(); }}
                    className="w-full border-emerald-600 text-emerald-700 rounded-full cursor-pointer h-9"
                  >
                    <FileText className="w-4 h-4 mr-1.5" />
                    {t('nav.brochure')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
